import { WorkspaceImpl } from '@affine/core/modules/workspace/impls/workspace';
import { nanoid, type Store, Text } from '@blocksuite/affine/store';
import * as Y from 'yjs';

export const ANNOTATION_KEY = 'mpxAnnotation';

export interface AnnotationTarget {
  id: string;
  kind: string;
  name: string;
  data: Y.Map<unknown>;
}

export type PropertyType = 'text' | 'number' | 'boolean' | 'date' | 'url';
export interface AnnotationProperty {
  name: string;
  type: PropertyType;
  value: string | number | boolean | null;
}

export interface ObjectAnnotation {
  version: 1;
  labels: Record<string, string>;
  content: Record<string, string>;
  tags?: Record<string, boolean>;
  properties?: Record<string, AnnotationProperty>;
  status?: string;
  aiArmed?: boolean;
  chat?: Record<string, AnnotationChatMessage>;
}

export interface AnnotationChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  time: string;
  iteration?: string;
}

function toBase64(data: Uint8Array): string {
  let binary = '';
  for (const byte of data) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(data: string): Uint8Array {
  return Uint8Array.from(atob(data), c => c.charCodeAt(0));
}

/** Attached directly to the object's Y.Map, so deletion/undo retain ownership. */
export function getAnnotation(target: AnnotationTarget, parent: Store) {
  const value = target.data.get(ANNOTATION_KEY);
  if (value instanceof Y.Map) {
    if (!parent.readonly)
      parent.transact(() => {
        if (!value.has('chat'))
          value.set('chat', new Y.Map<AnnotationChatMessage>());
        if (!value.has('tags')) value.set('tags', new Y.Map<boolean>());
        if (!value.has('properties'))
          value.set('properties', new Y.Map<AnnotationProperty>());
      });
    return value as Y.Map<unknown>;
  }
  if (parent.readonly) return null;

  const annotation = new Y.Map<unknown>();
  const labels = new Y.Map<string>();
  const content = new Y.Map<string>();
  const tags = new Y.Map<boolean>();
  const chat = new Y.Map<AnnotationChatMessage>();
  const properties = new Y.Map<AnnotationProperty>();
  // Surface serialize/copy turns nested maps into JSON; rehydrate on first edit.
  if (value && typeof value === 'object') {
    const saved = value as ObjectAnnotation;
    annotation.set('status', saved.status ?? 'none');
    annotation.set('aiArmed', saved.aiArmed === true);
    Object.entries(saved.chat ?? {}).forEach(([key, value]) =>
      chat.set(key, value)
    );
    Object.entries(saved.tags ?? {}).forEach(([key, value]) =>
      tags.set(key, value)
    );
    Object.entries(saved.properties ?? {}).forEach(([key, value]) =>
      properties.set(key, value)
    );
    Object.entries(saved.labels ?? {}).forEach(([key, color]) =>
      labels.set(key, color)
    );
    Object.entries(saved.content ?? {}).forEach(([key, update]) =>
      content.set(key, update)
    );
  }
  if (!annotation.has('status')) annotation.set('status', 'none');
  annotation.set('version', 1);
  annotation.set('labels', labels);
  annotation.set('content', content);
  annotation.set('tags', tags);
  annotation.set('chat', chat);
  annotation.set('properties', properties);
  parent.transact(() => target.data.set(ANNOTATION_KEY, annotation));
  return annotation;
}

/**
 * Native BlockSuite editor. Per-session Yjs states live inside the owner object.
 * Different sessions use different map keys, allowing concurrent CRDT edits to
 * merge rather than overwriting another user's note. No secondary server or DB.
 */
export function createAnnotationStore(
  parent: Store,
  annotation: Y.Map<unknown>
) {
  const content = annotation.get('content') as Y.Map<string>;
  const collection = new WorkspaceImpl({
    rootDoc: new Y.Doc(),
    blobSource: {
      name: 'object-annotations',
      readonly: parent.readonly,
      get: key => parent.blobSync.get(key),
      set: (key, blob) => parent.blobSync.set(key, blob),
      delete: key => parent.blobSync.delete(key),
      list: () => parent.blobSync.list(),
    },
  });
  collection.meta.initialize();
  const doc = collection.createDoc('object-annotation');
  const store = doc.getStore();
  // Deterministic empty structure prevents duplicate roots on simultaneous opens.
  const clientID = doc.spaceDoc.clientID;
  doc.spaceDoc.clientID = 1;
  store.load(() => {
    const root = store.addBlock('affine:page', {
      id: 'annotation-root',
      title: new Text(''),
    });
    const note = store.addBlock('affine:note', { id: 'annotation-note' }, root);
    store.addBlock(
      'affine:paragraph',
      { id: 'annotation-paragraph', text: new Text('') },
      note
    );
  });
  doc.spaceDoc.clientID = clientID;
  const remoteOrigin = Symbol('object-annotation-remote');
  const applied = new Map<string, string>();
  const restore = () => {
    content.forEach((state, key) => {
      if (applied.get(key) !== state) {
        Y.applyUpdate(doc.spaceDoc, fromBase64(state), remoteOrigin);
        applied.set(key, state);
      }
    });
  };
  restore();
  store.resetHistory();
  store.readonly = parent.readonly;
  const session = nanoid();
  const save = (_: Uint8Array, origin: unknown) => {
    if (
      origin === remoteOrigin ||
      parent.readonly ||
      !targetIsAttached(annotation)
    )
      return;
    const state = toBase64(Y.encodeStateAsUpdate(doc.spaceDoc));
    applied.set(session, state);
    // Keep canvas undo separate from annotation typing (the note has its own undo).
    parent.spaceDoc.transact(() => content.set(session, state), remoteOrigin);
  };
  doc.spaceDoc.on('update', save);
  content.observe(restore);
  return {
    store,
    dispose: () => {
      doc.spaceDoc.off('update', save);
      content.unobserve(restore);
      collection.dispose();
      collection.doc.destroy();
    },
  };
}

function targetIsAttached(annotation: Y.Map<unknown>) {
  // Deleted objects keep their annotation for undo but cannot be edited.
  return (
    annotation.doc !== null &&
    !(annotation as unknown as { _item?: { deleted: boolean } })._item?.deleted
  );
}
